import { ZERO_WEI } from '@kwenta/sdk/constants'
import { FC, useCallback } from 'react'

import { setOpenModal } from 'state/app/reducer'
import { handleOneClickOperation } from 'state/futures/actions'
import {
	selectTradingMode,
	selectMarketAsset,
	selectTradeOrderType,
	selectTradePanelInputs,
} from 'state/futures/common/selectors'
import {
	grantMarginEnginePermission,
	submitCrossMarginTradePanelOrder,
} from 'state/futures/snxPerpsV3/actions'
import {
	selectCrossMarginTradePreview,
	selectKeeperUsdDepositExceedsBal,
	selectMarginEnginePermitted,
	selectMargineEngineEnabled,
	selectTradePanelKeeperDepositV3,
} from 'state/futures/snxPerpsV3/selectors'
import { TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import TradeConfirmationModal from './TradeConfirmationModal'

const CrossMarginOrderConfirmationModal: FC = () => {
	const dispatch = useAppDispatch()

	const { stopLossPrice, takeProfitPrice } = useAppSelector(selectTradePanelInputs)

	const marketAsset = useAppSelector(selectMarketAsset)
	const preview = useAppSelector(selectCrossMarginTradePreview)
	const orderType = useAppSelector(selectTradeOrderType)
	const marginEnginePermitted = useAppSelector(selectMarginEnginePermitted)
	const usdDeposit = useAppSelector(selectTradePanelKeeperDepositV3)
	const usdDepositExceedsBalance = useAppSelector(selectKeeperUsdDepositExceedsBal)
	const marginEngineEnabled = useAppSelector(selectMargineEngineEnabled)
	const tradingMode = useAppSelector(selectTradingMode)

	const onDismiss = useCallback(() => {
		dispatch(setOpenModal(null))
	}, [dispatch])

	const handleConfirmOrder = useCallback(() => {
		dispatch(submitCrossMarginTradePanelOrder())
	}, [dispatch])

	const handleSubmit = useCallback(async () => {
		tradingMode === TradingModes.ONE_CLICK
			? dispatch(handleOneClickOperation(handleConfirmOrder))
			: handleConfirmOrder()
	}, [dispatch, tradingMode, handleConfirmOrder])

	const handleApproveMarginEngine = useCallback(() => {
		dispatch(grantMarginEnginePermission())
	}, [dispatch])

	return (
		<TradeConfirmationModal
			perpsVersion={3}
			onDismiss={onDismiss}
			orderType={orderType}
			marketAsset={marketAsset}
			onSubmitOrder={
				marginEnginePermitted || !marginEngineEnabled ? handleSubmit : handleApproveMarginEngine
			}
			onApproveAllowance={() => {
				// TODO: Allowance?
			}}
			sltp={{
				stopLossPrice: stopLossPrice?.toString(),
				takeProfitPrice: takeProfitPrice?.toString(),
			}}
			tradePreview={{
				priceImpact: preview?.priceImpact,
				size: ZERO_WEI,
				price: preview?.fillPrice ?? ZERO_WEI,
				exceedsPriceProtection: false,
				sizeDelta: preview?.sizeDelta ?? ZERO_WEI,
				liqPrice: preview?.liqPrice ?? ZERO_WEI,
			}}
			submitLabelI18n={
				marginEnginePermitted || !marginEngineEnabled
					? undefined
					: 'futures.market.trade.confirmation.modal.approve-margin-engine'
			}
			allowanceValid
			keeperDepositExceedsBalance={usdDepositExceedsBalance}
			fees={{
				tradeFee: preview?.fee ?? ZERO_WEI,
				executionFee: preview?.settlementFee ?? ZERO_WEI,
				keeperFee: usdDeposit,
			}}
		/>
	)
}

export default CrossMarginOrderConfirmationModal
